import { useState, useEffect, useMemo } from 'react';
import { Box, Card, Container, useTheme } from '@mui/material';
import { ContentLoadingError, CustomWaterLoading, RevoGridTable } from 'src/components';
import { ColumnRegular } from '@revolist/react-datagrid';
import { useNavigate } from 'react-router-dom';
import { StyledSearchBox } from 'src/features/customer-accounts/pages/style';
import { SITES_DASHBOARD_PATH } from 'src/routes/config';
import { CustomerService } from 'src/services/customers/customer.service';
import { SiteSearchBar } from 'src/features/sites/components/search';
import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import { SiteForGlobalAdmin } from '../types';

const GlobalAdminSitesManagement = () => {
  const navigate = useNavigate();

  const theme = useTheme();

  const siteColumns: ColumnRegular[] = [
    { prop: 'siteName', name: 'Site Name', size: 200, readonly: true, order: 'asc' },
    { prop: 'accountName', name: 'Account Name', size: 300, readonly: true },
    { prop: 'siteType', name: 'Site Type', size: 200, readonly: true },
    { prop: 'address', name: 'Site Address', size: 300, readonly: true },
  ];

  const [sites, setSites] = useState<{ value: SiteForGlobalAdmin[] | null; loaded: boolean }>({
    value: [],
    loaded: false,
  });
  const [siteSearchTerm, setSiteSearchTerm] = useState('');

  useDocumentTitle('Streametric - All Sites');

  useEffect(() => {
    CustomerService.getSitesForAllCustomers()
      .then((data: SiteForGlobalAdmin[]) => {
        setSites({ value: data, loaded: true });
      })
      .catch((error) => {
        setSites({ value: null, loaded: true });
        console.error(error);
      });
  }, []);

  const filteredSites = useMemo(() => {
    const term = siteSearchTerm.trim().toLowerCase();
    const allSites = sites.value ?? [];
    return term
      ? allSites.filter((site) => (site.siteName ?? '').toLowerCase().includes(term))
      : allSites;
  }, [sites.value, siteSearchTerm]);

  const getSiteDashboardUrl = (site: SiteForGlobalAdmin) =>
    SITES_DASHBOARD_PATH + `?customerId=${site.customerId}&siteId=${site.id}`;

  const handleOpenSite = (site: SiteForGlobalAdmin) => {
    navigate(getSiteDashboardUrl(site));
  };

  return (
    <Container maxWidth={false} disableGutters data-sm="customer-account-content">
      <Card sx={{ mb: '32px', backgroundColor: theme.palette.background.default }}>
        <StyledSearchBox>
          <SiteSearchBar
            searchValue={siteSearchTerm}
            setSearchValue={setSiteSearchTerm}
            placeholder="Search by Site Name..."
          />
        </StyledSearchBox>
        {!sites.loaded && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              width: '100%',
            }}
          >
            <CustomWaterLoading />
          </Box>
        )}

        {sites.loaded && sites.value === null && (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <ContentLoadingError />
          </Box>
        )}

        {sites.loaded && sites.value !== null && (
          <RevoGridTable
            sortable
            filterable
            columns={siteColumns}
            source={filteredSites}
            maxVisibleRows={15}
            onRowDoubleClick={(row) => handleOpenSite(row as unknown as SiteForGlobalAdmin)}
            sx={{ mb: '24px', ml: 2, mr: 2 }}
          />
        )}
      </Card>
    </Container>
  );
};

export default GlobalAdminSitesManagement;
